import type { SharedStateDelayedAction } from '../api/ReplicantAPI';
import { ErrorReporter } from '../common/ErrorReporter';
import DB, {
    ConditionalWriteError,
    Entry,
    SharedStateGlobalItem,
    SharedStateMessageItem,
    SharedStateUserItem,
    UserSharedStateItems,
} from '../db/DB';
import { ReplicantError } from '../Errors';
import logger from '../logger';
import { generateSharedStateIndexDoc } from '../ReplicantComputedProperties';
import type { ReplicantConfig } from '../ReplicantConfig';
import type ReplicantMigrator from '../ReplicantMigrator';
import {
    SharedState,
    SharedStateCounterMap,
    sharedStateHasSearchableProperties,
    SharedStates,
    SharedStateSystemFields,
    WithSharedStateSystemFields,
} from '../ReplicantSharedStates';
import type SB from '../SchemaBuilder';
import { partition } from '../utils/ArrayUtils';
import {
    getApplicableMessages,
    getUnreducedMessagesForDeletion,
    isMessageRecentlyReduced,
    updateRecentlyReducedMessages,
    warnOnOversizeMessages,
} from '../utils/MessageUtils';
import { mapObject } from '../utils/ObjUtils';
import { duration, unreachable } from '../utils/Utils';
import { migrateAndFillDefaults } from './migrateAndFillDefaults';
import * as perf from './performanceTracking';
import type { PushSharedStateDocToIndexFn } from './ServerReplicant';

const INITIAL_MIGRATION_VERSION = 1;
export const REDUCE_MESSAGES_MIN_THRESHOLD = 100;

export type SharedStateIndexDocs = { [stateName: string]: { [stateId: string]: { [indexProperty: string]: unknown } } };

export type UnknownSharedState = {
    counters: SharedStateCounterMap;
    global?: WithSharedStateSystemFields<Record<string, unknown>>;
    users: Record<string, unknown>;
};

export default class ServerReplicantSharedStates {
    constructor(
        private opts: {
            config: ReplicantConfig<any>;
            db: DB<any>;
            dateNow: () => number;
            errorReporter: ErrorReporter;
            isInitialDevelopment: () => boolean;
            pushSharedStateDocToIndex: PushSharedStateDocToIndexFn;
        },
    ) {}

    async resolveAllSharedStatesUpdates(updates: SharedStateDelayedAction[]): Promise<SharedStateIndexDocs> {
        const updatesByState: { [stateId: string]: SharedStateDelayedAction[] } = {};

        for (let i = 0; i < updates.length; i++) {
            const update = updates[i]!;
            const { stateId } = update;

            const laterStateUpdates = updates.slice(i + 1).filter((u) => u.stateId === stateId);

            // Skip user state update if later updates set or delete the same user state:
            if (
                (update.type === 'deleteUserSharedState' || update.type === 'setUserSharedState') &&
                laterStateUpdates.find((u) => u.type === 'deleteUserSharedState' || u.type === 'setUserSharedState')
            ) {
                continue;
            }

            if (update.type === 'setUserSharedState') {
                // Use the first revision number in case of multiple user state writes:
                const firstUserStateSetUpdate = updates.find(
                    (u): u is SharedStateDelayedAction & { type: 'setUserSharedState' } =>
                        u.stateId === stateId && u.type === 'setUserSharedState',
                )!;

                update.userState.rev = firstUserStateSetUpdate.userState.rev;

                // Skip user state write if state value did not change:
                const changesStateValue =
                    !firstUserStateSetUpdate.previousStateValue ||
                    JSON.stringify(update.userState.state) !== firstUserStateSetUpdate.previousStateValue;

                if (!changesStateValue) {
                    continue;
                }
            }

            // Skip counter set or increase if a later update sets the same counter:
            if (
                (update.type === 'increaseSharedStateCounter' || update.type === 'setSharedStateCounter') &&
                laterStateUpdates.find((u) => u.type === 'setSharedStateCounter' && u.counter === update.counter)
            ) {
                continue;
            }

            if (update.type === 'increaseSharedStateCounter') {
                // Skip counter increase of 0:
                if (update.increment === 0) {
                    continue;
                }

                // Merge all increases of the same counter into a single increase update:
                const laterIncrease = laterStateUpdates.find(
                    (u): u is SharedStateDelayedAction & { type: 'increaseSharedStateCounter' } =>
                        u.type === 'increaseSharedStateCounter' && u.counter === update.counter,
                );

                if (laterIncrease) {
                    laterIncrease.increment += update.increment;

                    continue;
                }
            }

            if (!updatesByState[stateId]) {
                updatesByState[stateId] = [];
            }

            updatesByState[stateId]!.push(update);
        }

        const statePromises: Promise<void>[] = [];
        const newIndexDocuments: { [stateName: string]: { [stateId: string]: { [indexProperty: string]: unknown } } } =
            {};

        for (const stateId in updatesByState) {
            const stateUpdates = updatesByState[stateId]!;
            const stateName = stateUpdates[0]!.stateName;

            statePromises.push(
                this.resolveSharedStateUpdates({ stateId, stateName, updates: stateUpdates }).then((indexDocument) => {
                    if (indexDocument) {
                        if (!newIndexDocuments[stateName]) {
                            newIndexDocuments[stateName] = {};
                        }

                        newIndexDocuments[stateName]![stateId] = indexDocument;
                    }
                }),
            );
        }

        await Promise.all(statePromises);

        return newIndexDocuments;
    }

    async fetchSharedState(opts: {
        consistentRead?: boolean;
        excludeMessageIds?: string[];
        reduceMessages?: boolean;
        stateName: string;
        stateId: string;
    }): Promise<{ consumedCapacity: number; sharedState?: UnknownSharedState }> {
        const { consistentRead, stateId, stateName } = opts;

        const config = this.tryGetStateConfig(stateName);

        const [readResponse, counters] = await Promise.all([
            this.opts.db.sharedStates.fetchSharedState({ stateId, stateName, consistentRead }),

            this.fetchCounters({ stateId, stateName }),
        ]);
        const consumedCapacity = readResponse.consumedCapacity;
        let sharedState = readResponse.sharedState;

        const globalStateNotFound = config.schema.global && !sharedState?.globalSharedState;

        // Try re-reading just the global shared state item consistently, in case it was created recently:
        if (globalStateNotFound && !consistentRead) {
            const globalSharedState = await this.opts.db.sharedStates.fetchGlobalSharedState({
                stateId,
                stateName,
                consistentRead: true,
            });
            if (globalSharedState && globalSharedState.stateName === stateName) {
                sharedState = {
                    globalSharedState,
                    sharedStateMessages: sharedState?.sharedStateMessages ?? [],
                    userSharedStates: sharedState?.userSharedStates ?? {},
                };
            }
        }

        if (!sharedState || (sharedState.globalSharedState && sharedState.globalSharedState.stateName !== stateName)) {
            return { consumedCapacity }; // No state for this ID
        }

        /**
         * 150 RCUs means 150 * 2 eventually consistent reads up to 4KB = 1,200 KB.
         * With the 400KB query page size in DynamoDB this adds up to 3 consecutive query requests.
         *
         * In practice this gives us around 6,600 user shared states with a small state value like `{ score: 1234 }`, ignoring the global state and message items.
         */
        const FETCH_SHARED_STATE_MAX_RCU_WARNING_THRESHOLD = 150;

        if (readResponse.consumedCapacity > FETCH_SHARED_STATE_MAX_RCU_WARNING_THRESHOLD) {
            const message =
                'Shared state size in storage exceeds the suggested limit. To avoid performance issues, either reduce the number of user states or remove unused state properties.';

            const warning = new ReplicantError(message, 'server_error', 'shared_state_too_large', 'warning', {
                consumedCapacity: readResponse.consumedCapacity,
                messageCount: sharedState.sharedStateMessages.length,
                stateId,
                stateName,
                userCount: Object.keys(sharedState.userSharedStates).length,
            });

            await this.opts.errorReporter.captureException(warning);
        }

        const userSchema = config.schema.user;
        const userSharedStates = userSchema
            ? mapObject(sharedState.userSharedStates, (_userId, item) => {
                  this.migrateAndFillDefaults(userSchema, item);

                  return item.state;
              })
            : {};

        if (!config.schema.global) {
            return { consumedCapacity, sharedState: { counters: {}, global: undefined, users: userSharedStates } };
        }

        const globalItem = sharedState.globalSharedState;
        if (!globalItem) {
            return { consumedCapacity };
        }

        this.migrateAndFillDefaults(config.schema.global, globalItem);

        const messages = sharedState.sharedStateMessages.filter(
            (msg) => !(opts.excludeMessageIds ?? []).includes(msg.id),
        );

        const fullSharedState = {
            counters,
            global: getGlobalStateWithSystemFields(globalItem, counters),
            users: userSharedStates,
        };

        if (messages.length) {
            const { reducedMessages, unreducedMessages } = this.applyMessages({
                messages,
                recentMessageReductionLog: globalItem.recentMessageReductionLog,
                sharedState: fullSharedState,
                stateId,
                stateName,
            });

            if (reducedMessages.length > 0) {
                const lastMessageAt = Math.max(...reducedMessages.map((msg) => msg.timestamp));
                const newUpdatedAt = Math.max(fullSharedState.global.updatedAt, lastMessageAt);

                setSharedStateSystemField(fullSharedState.global, 'updatedAt', newUpdatedAt);
            }

            // A newer backend version may have migrated global item to a version higher than this backend version's latest migration: in this case skip reducing messages to avoid overwriting the migration.
            const latestMigrationVersionInConfig =
                config.schema.global.migrator?.getLatestVersion() ?? INITIAL_MIGRATION_VERSION;
            const isMigratedToLatestVersion = globalItem.version === latestMigrationVersionInConfig;

            const reduceMessages =
                opts.reduceMessages &&
                reducedMessages.length + unreducedMessages.length >= REDUCE_MESSAGES_MIN_THRESHOLD &&
                isMigratedToLatestVersion;

            if (reduceMessages) {
                if (reducedMessages.length > 0) {
                    updateRecentlyReducedMessages(this.opts.dateNow(), globalItem, reducedMessages);

                    globalItem.rev++;
                    globalItem.updatedAt = Math.max(
                        globalItem.updatedAt,
                        ...reducedMessages.map((msg) => msg.timestamp),
                    );
                    globalItem.state = getGlobalStateWithoutSystemFields(fullSharedState.global);

                    // Report Sentry warning if global item size is close to the 400KB DynamoDB item size limit
                    const globalItemSize = JSON.stringify(globalItem).length;
                    if (globalItemSize > 350_000) {
                        await this.opts.errorReporter.captureException(
                            new ReplicantError(
                                'Shared state global item size exceeds 350 KB uncompressed. Reduce global item size to improve performance and avoid hitting the 400 KB compressed hard limit.',
                                'server_error',
                                'shared_state_too_large',
                                'warning',
                                {
                                    sharedStateId: stateId,
                                    sharedStateName: stateName,
                                    globalItemSize,
                                },
                            ),
                        );
                    }

                    try {
                        await this.opts.db.sharedStates.writeGlobalSharedState(globalItem);
                    } catch (e) {
                        if (e instanceof ConditionalWriteError) {
                            // Everyone's racing to do this, and someone beat us to it. This is fine.
                        } else {
                            throw e; // An actual error.
                        }
                    }
                }

                const unreducedMessagesForDeletion = getUnreducedMessagesForDeletion(
                    this.opts.dateNow(),
                    unreducedMessages,
                );

                await this.opts.db.sharedStates.deleteSharedStateMessages(
                    [...reducedMessages, ...unreducedMessagesForDeletion].map((message) => ({
                        messageId: message.id,
                        stateId,
                        stateName,
                    })),
                );
            }
        }

        return { consumedCapacity, sharedState: fullSharedState };
    }

    async fetchOrCreateSharedState(opts: { stateId: string; stateName: string }): Promise<UnknownSharedState> {
        const { stateId, stateName } = opts;

        try {
            const now = this.opts.dateNow();

            const newSharedState = getDefaultSharedState(stateId, now, this.tryGetStateConfig(stateName));

            await this.writeGlobalSharedState(stateName, newSharedState.global!);

            const indexDoc = this.getIndexDoc({ now, sharedState: newSharedState, stateName });

            if (indexDoc) {
                await this.opts.pushSharedStateDocToIndex({ indexDoc, stateId, stateName });
            }

            return newSharedState;
        } catch (error) {
            const alreadyExists = error instanceof ReplicantError && error.subCode === 'shared_state_duplicate_id';

            if (alreadyExists) {
                const { sharedState: existingSharedState } = await this.fetchSharedState({
                    stateId,
                    stateName,
                    consistentRead: true,
                });

                if (!existingSharedState) {
                    throw Error(`Shared state ${stateName} with ID ${stateId} not found`); // Should not happen as long as we're not deleting global shared states
                }

                return existingSharedState;
            }

            throw error;
        }
    }

    setUserState(opts: {
        entry: Entry<any>;
        state: Record<string, unknown>;
        userId: string;
        stateName: string;
        stateId: string;
    }): SharedStateUserItem<any, any> {
        const config = this.tryGetStateConfig(opts.stateName);

        const userSchema = config.schema.user;
        if (!userSchema) {
            throw new Error(`Cannot set user shared state ${opts.stateName} for ${opts.stateId}: No user schema.`);
        }

        userSchema.schema.tryValidate(opts.state);

        const userSharedStates = opts.entry.userSharedStates;

        const userState: SharedStateUserItem<any, any> = {
            state: opts.state,

            userId: opts.userId,
            stateId: opts.stateId,
            stateName: opts.stateName,

            rev: userSharedStates[opts.stateName]?.[opts.stateId]?.rev ?? 0,
            version: userSchema.migrator?.getLatestVersion() ?? INITIAL_MIGRATION_VERSION,
        };

        this.migrateAndFillDefaults(userSchema, userState);

        ++userState.rev;

        userSharedStates[opts.stateName] = {
            ...userSharedStates[opts.stateName],
            [opts.stateId]: userState,
        };

        return userState;
    }

    /**
     * @returns A function that writes migrated user shared states to DB, or `undefined` if there were no items to migrate.
     */
    migrateUserSharedStates(userSharedStates: UserSharedStateItems<any> | undefined): (() => Promise<void>) | void {
        if (!userSharedStates) {
            return;
        }

        const sharedStatesConfig: SharedStates | undefined = this.opts.config.sharedStates;

        const migratedItems: { [stateId: string]: { rev: number } } = {};

        for (const stateName in userSharedStates) {
            const config = sharedStatesConfig?.[stateName]?.schema.user;
            if (!config) {
                continue;
            }

            const userSharedStatesByName = userSharedStates[stateName];
            for (const stateId in userSharedStatesByName) {
                const item = userSharedStatesByName[stateId];
                if (!item) {
                    continue;
                }

                const oldVersion = item.version;
                this.migrateAndFillDefaults(config, item);

                if (oldVersion !== item.version) {
                    migratedItems[stateId] = { rev: item.rev };
                }
            }
        }

        const modifiedUserSharedStates = Object.values(userSharedStates)
            .flatMap((statesById) => Object.values(statesById))
            // If a migratedItem doesn't exist for the state ID, we didn't change it.
            // If the item's revision is different, it got saved already (e.g. in onLogin).
            // In either case, skip item.
            .filter((item) => migratedItems[item.stateId]?.rev === item.rev);

        if (modifiedUserSharedStates.length) {
            const writeMigratedUserSharedStatesToDb = async (): Promise<void> => {
                await Promise.all(
                    modifiedUserSharedStates.map(async (item: SharedStateUserItem<any, any>) => {
                        ++item.rev;

                        await Promise.all([
                            this.opts.db.sharedStates.writeUserSharedState(item),

                            // TODO: remove after completing user shared state items migration
                            this.opts.db.sharedStates
                                .writeUserSharedState(item, { useLegacyKey: true })
                                .catch(() => undefined),
                        ]);
                    }),
                );
            };

            return writeMigratedUserSharedStatesToDb;
        }
    }

    private async fetchCounters(opts: { stateId: string; stateName: string }): Promise<SharedStateCounterMap> {
        const { stateId, stateName } = opts;

        const config = this.tryGetStateConfig(stateName);
        const counterNames = config.schema.global?.counters;

        if (!counterNames || counterNames.length === 0) {
            return {};
        }

        const dbCounters = await this.opts.db.sharedStates.counters.fetchCounters({ stateId, stateName });

        const counters: SharedStateCounterMap = {};

        for (const counterName of counterNames) {
            counters[counterName] = dbCounters[counterName] ?? { updatedAt: 0, value: 0 };
        }

        return counters;
    }

    private async resolveSharedStateUpdates(opts: {
        stateId: string;
        stateName: string;
        updates: SharedStateDelayedAction[];
    }): Promise<{ [indexProperty: string]: unknown } | void> {
        const { stateId, stateName, updates } = opts;

        const stateConfig = this.tryGetStateConfig(stateName);

        const now = this.opts.dateNow();
        const globalSchema = stateConfig.schema.global;
        const dbPromises: Promise<any>[] = [];
        const createsNewSharedState = updates.some((update) => update.type === 'createSharedState');
        const isIndexed = sharedStateHasSearchableProperties(stateConfig);

        // Avoid redundant shared state reads:
        const mustReadSharedState =
            isIndexed ||
            updates.some((u) => {
                const createsNewUserState = u.type === 'setUserSharedState' && u.userState.rev === 1;
                return u.type === 'postSharedStateMessage' || createsNewUserState;
            });

        let sharedState: UnknownSharedState | undefined;

        if (createsNewSharedState) {
            sharedState = getDefaultSharedState(stateId, now, stateConfig);

            if (globalSchema) {
                const writeGlobalStatePromise = this.writeGlobalSharedState(stateName, sharedState.global!);

                dbPromises.push(writeGlobalStatePromise);
            }
        } else if (mustReadSharedState) {
            // Exclude any flushed messages in shared state read to avoid applying flushed messages twice:
            const excludeMessageIds: string[] = [];

            for (const update of updates) {
                if (update.type === 'postSharedStateMessage' && update.flushed) {
                    excludeMessageIds.push(update.message.id);
                }
            }

            const didPostMessage = updates.some((update) => update.type === 'postSharedStateMessage');

            const fetchResult = await this.fetchSharedState({
                excludeMessageIds,
                stateName,
                stateId,
                reduceMessages: didPostMessage, // Only reduce shared state messages on message post
            });

            sharedState = fetchResult.sharedState;
        }

        const serializedOldIndexDoc = sharedState
            ? JSON.stringify(this.getIndexDoc({ now: sharedState.global?.updatedAt ?? 0, sharedState, stateName }))
            : undefined;

        const messagesToWrite: SharedStateMessageItem<any, any>[] = [];
        const countersToIncrease: { [counterName: string]: number } = {};

        for (const update of updates) {
            switch (update.type) {
                case 'createSharedState':
                    // Already handled above
                    break;
                case 'deleteUserSharedState': {
                    const deleteUserStatePromise = Promise.all([
                        this.opts.db.sharedStates.deleteUserSharedState({
                            stateId,
                            stateName,
                            userId: update.userId,
                        }),

                        // TODO: remove after completing user shared state items migration
                        this.opts.db.sharedStates
                            .deleteUserSharedState({
                                stateId,
                                stateName,
                                useLegacyKey: true,
                                userId: update.userId,
                            })
                            .catch(() => undefined),
                    ]);

                    dbPromises.push(deleteUserStatePromise);

                    delete sharedState?.users[update.userId];

                    break;
                }
                case 'postSharedStateMessage': {
                    if (!globalSchema) {
                        throw new Error(
                            'Trying to post messages to a shared state without a global schema, name: ' + stateName,
                        );
                    }

                    if (!sharedState?.global) {
                        throw new Error(
                            `Trying to post messages to a global shared state that does not exist: ${JSON.stringify(
                                update,
                            )}`,
                        );
                    }

                    const message: SharedStateMessageItem<any, any> = {
                        args: update.message.args,
                        id: update.message.id,
                        name: update.message.name,
                        sender: update.message.sender,
                        stateId,
                        stateName,
                        timestamp: this.opts.dateNow() + update.message.senderClockOffset,
                    };

                    // Skip DB write if message was already flushed:
                    if (!update.flushed) {
                        // Report Sentry warnings on oversized arguments:
                        await warnOnOversizeMessages([message], this.opts.errorReporter, {
                            sharedStateName: stateName,
                            sharedStateId: stateId,
                        });

                        messagesToWrite.push(message);
                    }

                    this.applyMessages({
                        sharedState,
                        isSampled: perf.isSampled(),
                        messages: [message],
                        stateName,
                        stateId,
                    });

                    break;
                }
                case 'setUserSharedState': {
                    const createsNewUserState = update.userState.rev === 1;

                    if (globalSchema && !sharedState?.global && createsNewUserState) {
                        throw new Error(
                            `Cannot set user shared state for ${stateName} id=${stateId}: global state does not exist.`,
                        );
                    }

                    const writeUserStatePromise = Promise.all([
                        this.opts.db.sharedStates.writeUserSharedState(update.userState),

                        // TODO: remove after completing user shared state items migration
                        this.opts.db.sharedStates
                            .writeUserSharedState(update.userState, { useLegacyKey: true })
                            .catch(() => undefined),
                    ]);

                    dbPromises.push(writeUserStatePromise);

                    if (sharedState) {
                        sharedState.users[update.userState.userId] = update.userState.state;
                    }

                    break;
                }
                case 'increaseSharedStateCounter': {
                    const oldCounterValue = sharedState?.counters[update.counter]?.value;

                    if (sharedState) {
                        if (oldCounterValue === undefined) {
                            throw Error(`Cannot increment counter ${stateName}.${update.counter}: invalid counter`);
                        }

                        const newCounterValue = oldCounterValue + update.increment;
                        const isGrowingOutOfBounds =
                            newCounterValue < Number.MIN_SAFE_INTEGER / 2 ||
                            newCounterValue > Number.MAX_SAFE_INTEGER / 2;

                        if (isGrowingOutOfBounds) {
                            const outOfBoundsWarning = new ReplicantError(
                                'Shared state counter value is reaching safe integer boundaries. Make sure the value stays between [MIN_SAFE_INTEGER, MAX_SAFE_INTEGER].',
                                'server_error',
                                'shared_state_invalid_counter_value',
                                'warning',
                                {
                                    counter: 'memberCount',
                                    counterValue: newCounterValue,
                                    sharedStateName: stateName,
                                    sharedStateId: stateId,
                                },
                            );

                            await this.opts.errorReporter.captureException(outOfBoundsWarning);
                        }
                    }

                    countersToIncrease[update.counter] = update.increment;

                    if (sharedState) {
                        sharedState.counters[update.counter] = {
                            updatedAt: now,
                            value: (oldCounterValue ?? 0) + update.increment,
                        };
                    }

                    break;
                }
                case 'setSharedStateCounter': {
                    const setCounterPromise = this.opts.db.sharedStates.counters.setCounter({
                        counterName: update.counter,
                        value: update.value,
                        now,
                        stateId,
                        stateName,
                    });

                    dbPromises.push(setCounterPromise);

                    if (sharedState) {
                        sharedState.counters[update.counter] = { updatedAt: now, value: update.value };
                    }

                    break;
                }
                default:
                    unreachable(update);
            }
        }

        if (messagesToWrite.length > 0) {
            dbPromises.push(this.opts.db.sharedStates.writeSharedStateMessages(messagesToWrite));
        }

        if (Object.keys(countersToIncrease).length > 0) {
            dbPromises.push(
                this.opts.db.sharedStates.counters.increaseCounters({
                    counters: countersToIncrease,
                    now,
                    stateId,
                    stateName,
                }),
            );
        }

        await Promise.all(dbPromises);

        if (serializedOldIndexDoc && sharedState) {
            const newIndexDoc = this.getIndexDoc({ now: this.opts.dateNow(), sharedState, stateName });

            const indexDocChanged = createsNewSharedState || serializedOldIndexDoc !== JSON.stringify(newIndexDoc);

            if (indexDocChanged) {
                return newIndexDoc;
            }
        }
    }

    private getIndexDoc(opts: {
        now: number;
        sharedState: UnknownSharedState;
        stateName: string;
    }): Record<string, unknown> | void {
        const { now, sharedState, stateName } = opts;

        const stateConfig = this.tryGetStateConfig(stateName);

        const isIndexed = sharedStateHasSearchableProperties(stateConfig);

        if (!isIndexed) {
            return;
        }

        const { counters, global, users } = sharedState;

        if (!global) {
            throw Error('Cannot index shared state without global schema');
        }

        return generateSharedStateIndexDoc({ counters, global, users }, stateConfig.computedProperties, () => now);
    }

    private async writeGlobalSharedState(
        stateName: string,
        state: WithSharedStateSystemFields<Record<string, unknown>>,
    ): Promise<SharedStateGlobalItem<any, any>> {
        const stateConfig = this.tryGetStateConfig(stateName);
        const globalSchema = stateConfig.schema.global;

        if (!globalSchema) {
            throw Error(`Cannot write global shared state: no global schema in ${stateName}`);
        }

        const globalItem: SharedStateGlobalItem<any, any> = {
            state: getGlobalStateWithoutSystemFields(state),
            stateId: state.id,
            stateName,
            createdAt: state.createdAt,
            updatedAt: state.updatedAt,
            rev: 0,
            version: globalSchema.migrator?.getLatestVersion() ?? INITIAL_MIGRATION_VERSION,
        };

        try {
            await this.opts.db.sharedStates.writeGlobalSharedState(globalItem);
        } catch (error) {
            if (error instanceof ConditionalWriteError) {
                throw new ReplicantError(
                    `Cannot create shared state ${stateName}: duplicate state ID ${state.id}`,
                    'server_error',
                    'shared_state_duplicate_id',
                );
            }

            throw error;
        }

        return globalItem;
    }

    private tryGetStateConfig(stateName: string): SharedState {
        const stateConfig = this.opts.config.sharedStates?.[stateName];
        if (!stateConfig) {
            throw Error(`Invalid shared state name ${stateName}`);
        }

        return stateConfig;
    }

    private migrateAndFillDefaults<TState>(
        config: { migrator?: ReplicantMigrator; schema: SB.Schema<TState> },
        entry: { state: TState; updatedAt?: number; version: number },
    ): void {
        if (!config.migrator) {
            return;
        }

        if (entry.version === 0) {
            // Fix states created before migrator support.
            entry.version = INITIAL_MIGRATION_VERSION;
        }

        try {
            migrateAndFillDefaults({ migrator: config.migrator, stateSchema: config.schema }, entry);
        } catch (e) {
            if (this.isInitialDevelopment(config.migrator)) {
                // During initial development, it's ok to return default states.
                entry.state = config.schema.getDefault()!;
                if ('updatedAt' in entry) {
                    entry.updatedAt = this.opts.dateNow();
                }
            } else {
                throw e;
            }
        }
    }

    private isInitialDevelopment(migrator: ReplicantMigrator) {
        return this.opts.isInitialDevelopment() && migrator.getLatestVersion() === INITIAL_MIGRATION_VERSION;
    }

    private applyMessages(opts: {
        isSampled?: boolean;
        messages: SharedStateMessageItem<any, any>[];
        recentMessageReductionLog?: { [key: string]: number };
        sharedState: UnknownSharedState;
        stateName: string;
        stateId: string;
    }) {
        const { messages, recentMessageReductionLog, sharedState, stateId, stateName } = opts;

        const unreducedMessages: typeof messages = [];
        const reportMessageError = (text: string, message: { timestamp: number }) => {
            logger.warn(text);

            // A message becomes old 23 hours after it's sent.
            const isOldMessage = this.opts.dateNow() - message.timestamp > duration({ hours: 23 });
            if (!isOldMessage) {
                // For new messages, assume this is an old version the client is playing on.
                // A fix is probably already released and this will get reduced next time.
                return;
            }

            // A fix was not released. Report the error.

            const error: Error & { stateContext?: any } = new Error(text);
            error.stateContext = { message, sharedState, stateId };
            void this.opts.errorReporter.captureException(error); // TODO await to prevent losing error reports
        };

        const stateConfig = this.tryGetStateConfig(stateName);
        const messagesConfig = stateConfig.messages ?? {};

        const applicableMessages = getApplicableMessages(messagesConfig, messages, {
            onUnknownMessage: (message) => {
                unreducedMessages.push(message);
                reportMessageError('Unknown message: ' + message.name, message);
            },
            onInvalidArgs: (message, validationError) => {
                unreducedMessages.push(message);
                const text = 'Error validating args for message `' + message.name + '`: ' + validationError;
                reportMessageError(text, message);
            },
        });

        const [messagesToReduce, reducedMessages] = partition(
            applicableMessages,
            (msg) => !!msg && !isMessageRecentlyReduced({ recentMessageReductionLog }, msg),
        );

        if (messagesToReduce.length > 0) {
            const stateBeforeMessages = JSON.stringify(sharedState.global);

            for (const message of messagesToReduce) {
                try {
                    const performanceContext: perf.PerformanceContext = {
                        type: 'sharedStateMessage',
                        name: message.name,
                        userId: stateId,
                    };

                    const reducer = messagesConfig[message.name]!.reducer;
                    const wrappedReducer = opts.isSampled
                        ? perf.withPerformanceTracking(reducer, performanceContext)
                        : reducer;

                    // TODO avoid `any` coercion
                    wrappedReducer(sharedState as any, message.args, {
                        senderId: message.sender,
                        timestamp: message.timestamp,
                    });

                    reducedMessages.push(message);
                } catch (e: any) {
                    const error = new ReplicantError(
                        `Error applying message ${message.name} to shared state ${stateName}: ${e}`,
                        'replication_error',
                        'message_errored',
                        'fatal',
                        {
                            args: JSON.stringify(message.args),
                            receiver: stateId,
                            sender: message.sender,
                            timestamp: message.timestamp,
                        },
                    );

                    // Preserve the message embedded in the stack, but override the stack itself.
                    const [, ...originalStackLines] = e.stack.split('\n');
                    const messageLine = error.stack?.split('\n')[0];
                    error.stack = [messageLine, ...originalStackLines].join('\n');

                    void this.opts.errorReporter.captureException(error); // TODO await to prevent losing error reports
                    unreducedMessages.push(message);
                }
            }

            // Validate that the global shared state is still valid
            const globalStateWithoutSystemFields = getGlobalStateWithoutSystemFields(sharedState.global!);

            const validationErrorAfterMessages =
                stateConfig.schema.global!.schema.validate(globalStateWithoutSystemFields);

            if (validationErrorAfterMessages) {
                const errorMessage =
                    `Replication error: Applying messages ${JSON.stringify(messages)} ` +
                    `to shared state ${stateName} ` +
                    `failed with: ${validationErrorAfterMessages}. ` +
                    `Original shared state: ${stateBeforeMessages}. ` +
                    `Invalid state after messages: ${JSON.stringify(sharedState.global)}`;

                throw new ReplicantError(errorMessage, 'replication_error', 'invalid_message');
            }
        }

        return { reducedMessages, unreducedMessages };
    }
}

function getDefaultSharedState(stateId: string, now: number, stateConfig: SharedState): UnknownSharedState {
    const counters: SharedStateCounterMap = {};

    const counterNames = stateConfig.schema.global?.counters ?? [];
    for (const counterName of counterNames) {
        counters[counterName] = { updatedAt: 0, value: 0 };
    }

    const global = stateConfig.schema.global
        ? getGlobalStateWithSystemFields(
              {
                  createdAt: now,
                  state: stateConfig.schema.global.schema.getDefault(),
                  stateId,
                  updatedAt: now,
              },
              counters,
          )
        : undefined;

    return { counters, global, users: {} };
}

function getGlobalStateWithSystemFields(
    globalItem: Pick<SharedStateGlobalItem<any, any>, 'createdAt' | 'state' | 'stateId' | 'updatedAt'>,
    counters: SharedStateCounterMap,
): WithSharedStateSystemFields<Record<string, unknown>> {
    const maxCounterUpdatedAt = Math.max(...Object.values(counters).map((counter) => counter.updatedAt));
    const updatedAt = Math.max(maxCounterUpdatedAt, globalItem.updatedAt);

    const globalState = { ...(globalItem.state as Record<string, unknown>) };

    setSharedStateSystemField(globalState, 'id', globalItem.stateId);
    setSharedStateSystemField(globalState, 'createdAt', globalItem.createdAt);
    setSharedStateSystemField(globalState, 'updatedAt', updatedAt);

    return globalState as WithSharedStateSystemFields<Record<string, unknown>>;
}

export function setSharedStateSystemField<T extends keyof SharedStateSystemFields>(
    globalState: Record<string, unknown>,
    key: T,
    value: SharedStateSystemFields[T],
): void {
    Object.defineProperty(globalState, key, { value, configurable: true, enumerable: true });
}

function getGlobalStateWithoutSystemFields(
    globalState: WithSharedStateSystemFields<Record<string, unknown>>,
): Record<string, unknown> {
    const { id, createdAt, updatedAt, ...globalStateWithoutSystemFields } = globalState;

    return globalStateWithoutSystemFields;
}
